import type { APIShelfFile, PaginatedAPIDecorator, PaginatedAPIResults } from '~~/entities/api'
import type { ALL_FILE_TYPES, APIDataFile } from '~~/entities/files'
import type { FileTransfer } from '~~/lib/upload/file-downloader'

export function formatFileSize(size: number) {
  if (!size) return '0 Bytes'

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(size) / Math.log(1024))

  return `${parseFloat((size / 1024 ** i).toFixed(2))} ${sizes[i]}`
}

export function formatTradeValue(price: number): string {
  const threeDecimal = price.toFixed(3)

  if (threeDecimal.charAt(threeDecimal.length - 1) === '0') {
    return threeDecimal.substring(0, threeDecimal.length - 1)
  }

  return threeDecimal
}

export function decoratePaginatedAPIResponse({
  results,
  count,
  previous,
  next,
}: PaginatedAPIResults<any>): PaginatedAPIDecorator<any, any> {
  return {
    results,
    count,
    next: next ? Object.fromEntries(new URL(next).searchParams) : null,
    previous: previous ? Object.fromEntries(new URL(previous).searchParams) : null,
  }
}

export const applicationMimeTypes: Record<string, string> = {
  'application/pdf': '.pdf',
}

export const textMimeTypes: Record<string, string> = {
  'text/csv': '.csv',
  'text/plain': '.txt',
}

export const imageMimeTypes: Record<string, string> = {
  'image/avif': '.avif',
  'image/bmp': '.bmp',
  'image/gif': '.gif',
  'image/png': '.png',
  'image/jpg': '.jpg',
  'image/jpeg': '.jpeg',
  'image/svg+xml': '.svg',
  'image/webp': '.webp',
}

export const videoMimeTypes: Record<string, string> = {
  'video/mp4': '.mp4',
  'video/m4v': '.m4v',
  'video/x-m4v': '.m4v',
  'video/quicktime': '.mov',
}

export const mimeTypeMap: Record<string, string> = {
  ...textMimeTypes,
  ...imageMimeTypes,
  ...videoMimeTypes,
  ...applicationMimeTypes,
}

export function sanitizeFileName(name: string) {
  return name.replace(/[,"':;?!@#$%^&*()<>[\]{}\s]/g, '_')
}

export function isExtensionLess(name: string): boolean {
  return !name.includes('.')
}

export function getFileExtension(file: File | FileTransfer | APIDataFile | APIShelfFile | { name: string }) {
  const parts = file.name.split('.')
  if (parts.length < 2) return '' // Files without a "." in the file name will only have 1 part
  return String(parts.at(-1)).toLowerCase()
}

export function isFileAllowed(file: File | FileTransfer | APIDataFile | APIShelfFile, acceptedTypes: string[] | ALL_FILE_TYPES): boolean {
  if (!acceptedTypes.length || acceptedTypes === '*') return true
  for (let i = 0; i < acceptedTypes.length; i++) {
    if (acceptedTypes[i].includes(getFileExtension(file))) {
      return true
    }
  }
  return false
}

/**
 * Matches a file against accepted types by full filename suffix, so multi-part
 * extensions (e.g. `.ers.xml`) match only the exact suffix instead of any `.xml` file.
 */
export function fileMatchesAcceptedType(file: File | FileTransfer | APIDataFile | APIShelfFile, acceptedTypes: string[]): boolean {
  const name = file.name.toLowerCase()
  return acceptedTypes.some(type => name.endsWith(type.toLowerCase()))
}

export function formatMimeType(type: string): string | undefined {
  return mimeTypeMap[type]
}

export function formatAcceptedTypes(acceptedTypes: string[] | ALL_FILE_TYPES): string {
  if (acceptedTypes === '*') {
    return acceptedTypes
  }
  return [...new Set(acceptedTypes)].join(', ')
}

export function getDeckNDELink(id: string | number) {
  const config = useRuntimeConfig()
  return `${config.public.WEB_ENDPOINT}editor/decks/${id}`
}

/**
   See if the string representation of this base 10 number is exactly equal to the number itself.
   @param value string containing a number
   @returns boolean indicating if the representation was identical
 */
function isNumeric(value: string) {
  return /^\d+$/.test(value?.toString().trim())
}

/**
   Parse a hyphen-delimited range, e.g. 6-10, 5-21
   Start must be ≤ the end of the range.
   @param stringRepresentation a string such as 6-10, 5-21
   @returns an array of numbers inside the range (inclusive of start and end), or undefined if not valid
 */
function parseRange(stringRepresentation: string) {
  const [from, to] = stringRepresentation.split('-')
  if (isNumeric(from) && isNumeric(to) && +to >= +from) {
    return Array.from(Array(+to - +from + 1)).map((x, i) => i + +from)
  }
}

/**
   Parse a user-provided, comma-delimited list of numbers and ranges
   @param userInput a string user input, e.g. 1, 2, 4-6, 10
   @returns A validated array of numbers, or null if the input wasn't well-formed.
 */
export function parseRanges(userInput: string): number[] | null {
  // strip whitespace
  const noWhitespace = userInput.replace(/\s/g, '')

  // split and remove blanks
  const ranges = noWhitespace.split(',').filter(x => x)

  // explode ranges
  const expandedRanges = ranges.reduceRight((acc: number[], cur: string) => {
    if (typeof cur !== 'string') {
      return acc
    }

    // single page - convert to positive number
    if (isNumeric(cur) && +cur > 0) {
      acc.unshift(+cur)
      return acc
    }

    // range of pages - add all of them in
    const explodedRange = parseRange(cur)
    if (explodedRange) {
      acc.unshift(...explodedRange)
    }

    return acc
  }, [])

  // Make unique
  return Array.from(new Set(expandedRanges))
}

const collator = new Intl.Collator('en', {
  usage: 'sort',
  numeric: true,
  caseFirst: 'upper',
  ignorePunctuation: true,
})

export function compare(a: string, b: string) {
  return collator.compare(a, b)
}

export const isInIframe = () => {
  if (import.meta.client) {
    return window.self !== window.top
  }
}

const now = () => performance.now()
const yieldToBrowser = () => new Promise<void>(r => requestAnimationFrame(() => r()))

export async function reduceTimeSliced<T, A>(
  array: readonly T[],
  reducer: (acc: A, item: T, index: number) => A,
  initial: A,
  {
    budgetMs = 10,
    onProgress,
    signal,
  }: {
    budgetMs?: number
    onProgress?: (done: number, total: number) => void
    signal?: AbortSignal
  } = {},
): Promise<A> {
  const total = array.length
  let acc = initial
  let i = 0

  while (i < total) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')

    const start = now()
    while (i < total && now() - start < budgetMs) {
      acc = reducer(acc, array[i], i)
      i++
    }

    onProgress?.(i, total)
    await yieldToBrowser()
  }

  return acc
}
